import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const SystemOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase?.from('admin_statistics')?.select('*')?.single();

      if (!error) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const metrics = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: 'Users', color: 'blue' },
    { label: 'Active Requests', value: stats?.active_requests || 0, icon: 'ShoppingBag', color: 'green' },
    { label: 'Total Deals', value: stats?.total_deals || 0, icon: 'Package', color: 'purple' },
    { label: 'Completed Deals', value: stats?.completed_deals || 0, icon: 'CheckCircle', color: 'emerald' },
    { label: 'Total Revenue', value: `$${(stats?.total_revenue || 0)?.toLocaleString()}`, icon: 'DollarSign', color: 'amber' },
    { label: 'Deals Today', value: stats?.deals_today || 0, icon: 'TrendingUp', color: 'rose' }
  ];

  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-600',
    green: 'bg-green-500/10 text-green-600',
    purple: 'bg-purple-500/10 text-purple-600',
    emerald: 'bg-emerald-500/10 text-emerald-600',
    amber: 'bg-amber-500/10 text-amber-600',
    rose: 'bg-rose-500/10 text-rose-600'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">System Overview</h2>
        <p className="text-muted-foreground">Real-time platform performance indicators</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics?.map((metric, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses?.[metric?.color]}`}>
                <Icon name={metric?.icon} size={24} />
              </div>
            </div>
            <p className="text-3xl font-bold text-foreground mb-1">{metric?.value}</p>
            <p className="text-sm text-muted-foreground">{metric?.label}</p>
          </div>
        ))}
      </div>
      {/* User Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">User Distribution</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Buyers</span>
              <span className="text-foreground font-semibold">{stats?.total_buyers || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Travelers</span>
              <span className="text-foreground font-semibold">{stats?.total_travelers || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Verified Users</span>
              <span className="text-foreground font-semibold">{stats?.verified_users || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Platform Health</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-muted-foreground">All systems operational</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-muted-foreground">Database: Healthy</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-muted-foreground">API: Responsive</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemOverview;